import { PAGE_SIZE_DEFAULT, type Paginated } from "@seagames/contracts";
import {
	type EmptyState,
	type KeysetPagerProps,
	type PaginationProps,
	type SortDir,
	type SortState,
	useColumnVisibility,
	useFilterDraft,
	useUiStrings,
} from "@seagames/ui";
import { type QueryKey, type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useRef } from "react";
import { errorMessage } from "../api/messages";

/** The params every list page's URL carries — the shape `listSearch()` parses. */
export interface ListSearchParams {
	page?: number;
	page_size?: number;
	search?: string;
	sort?: string;
	dir?: SortDir;
}

type SortKeyOf<S extends ListSearchParams> = NonNullable<S["sort"]>;

/** What `listOptions` receives: paging resolved to numbers, the shared params as the URL has them. */
export interface ListQueryBase<K extends string> {
	page: number;
	page_size: number;
	search?: string;
	sort?: K;
	dir?: SortDir;
}

/** The pagination line's numbers, already formatted for the UI locale; `count` (raw) picks the plural. */
export type SummaryValues = {
	count: number;
	from: string;
	to: string;
	total: string;
};

/** The only navigate call the hook makes. `Route.useNavigate()` satisfies it. */
export type SearchNavigate<S> = (opts: { search: (prev: S) => S; replace?: boolean }) => unknown;

/**
 * `S` is inferred from `search` alone and `F` from `filters.empty` alone.
 * With an enum filter, annotate `fromUrl`'s return (`(s): MyFilters => …`):
 * TS widens the `""` in `s.type ?? ""` to `string` while it is still inferring.
 */
export interface UseListPageOptions<S extends ListSearchParams, F extends Record<string, unknown>, Row, Key extends QueryKey> {
	/** Offset paging (page numbers) — the default. Keyset lists pass `"keyset"` (see `UseKeysetListPageOptions`). */
	paging?: "offset";
	/** `Route.useSearch()`. */
	search: S;
	navigate: SearchNavigate<NoInfer<S>>;
	/** The page's `queryOptions` factory; spread `q` and add the page's own filters. */
	listOptions: (q: ListQueryBase<SortKeyOf<NoInfer<S>>>) => UseQueryOptions<Paginated<Row>, Error, Paginated<Row>, Key>;
	/** localStorage key for the Columns picker — never rename one, or saved choices are lost. */
	columnsKey: string;
	filters: {
		/** The cleared FilterBar. Module-level constant: `useFilterDraft` memoises on it. */
		empty: F;
		/** URL → FilterBar values (`""` = not set). */
		fromUrl: (search: NoInfer<S>) => NoInfer<F>;
		/** FilterBar values → URL params (`undefined` drops the param). */
		toUrl: (filters: NoInfer<F>) => Partial<NoInfer<S>>;
	};
	/** The pagination line in this table's words ("1–25 of 320 sub-agencies"); `empty` replaces it at 0 rows. */
	summary: { range: (values: SummaryValues) => string; empty?: string };
}

interface EmptyCopy {
	title: ReactNode;
	description?: ReactNode;
}

/**
 * Two modes, one hook:
 *
 *   paging: "offset" (default) — page numbers, `page` / `page_size` in the URL,
 *     `count(*) OVER()` on every page. For small admin lookups.
 *   paging: "keyset" — Prev / Next over an opaque cursor, for lists too long
 *     for OFFSET (Rule 0). See `UseKeysetListPageOptions`.
 *
 * The mode is fixed per page (it is a literal in the call), so the branch
 * below always calls the same hooks in the same order.
 */
export function useListPage<S extends KeysetSearchParams, Row, Key extends QueryKey>(
	options: UseKeysetListPageOptions<S, Row, Key>,
): KeysetListPage<Row>;
export function useListPage<S extends ListSearchParams, F extends Record<string, unknown>, Row, Key extends QueryKey>(
	options: UseListPageOptions<S, F, Row, Key>,
): OffsetListPage<S, F, Row, Key>;
export function useListPage(
	// biome-ignore lint/suspicious/noExplicitAny: overload implementation; the public signatures above are typed
	options: any,
	// biome-ignore lint/suspicious/noExplicitAny: overload implementation
): any {
	// biome-ignore lint/correctness/useHookAtTopLevel: `paging` is a literal per call site, never changes between renders
	return options.paging === "keyset" ? useKeysetListPage(options) : useOffsetListPage(options);
}

type OffsetListPage<S extends ListSearchParams, F extends Record<string, unknown>, Row, Key extends QueryKey> = ReturnType<typeof useOffsetListPage<S, F, Row, Key>>;

/**
 * The wiring every admin list page repeats — URL ⇄ query ⇄ FilterBar ⇄
 * DataTable — and nothing else. Pages keep their columns, filter fields, row
 * actions and copy as JSX; spread the results:
 *
 *   <FilterBar {...page.filterBar}>…fields on page.draft / page.setDraft…</FilterBar>
 *   <DataTable columns={…} rowKey={…} empty={page.emptyState({…})} {...page.table} />
 *
 * Filters are drafted locally and reach the URL only on "Filter" (platform
 * rule — no per-keystroke fetches). Changing any filter / sort / page size
 * resets to page 1 and replaces the history entry; `page` itself pushes one.
 */
function useOffsetListPage<S extends ListSearchParams, F extends Record<string, unknown>, Row, Key extends QueryKey>({
	search,
	navigate,
	listOptions,
	columnsKey,
	filters: { empty, fromUrl, toUrl },
	summary,
}: UseListPageOptions<S, F, Row, Key>) {
	const page = search.page ?? 1;
	const pageSize = search.page_size ?? PAGE_SIZE_DEFAULT;
	const list = useQuery(
		listOptions({ page, page_size: pageSize, search: search.search, sort: search.sort as SortKeyOf<S> | undefined, dir: search.dir }),
	);
	const columnVisibility = useColumnVisibility(columnsKey);
	const { formatNumber: fmt } = useUiStrings();

	const patch = useCallback(
		(next: Partial<S>) => void navigate({ search: (prev) => ({ ...prev, page: undefined, ...next }), replace: true }),
		[navigate],
	);

	const filters = useFilterDraft<F>(
		fromUrl(search),
		useCallback((f: F) => patch(toUrl(f)), [patch, toUrl]),
		empty,
	);
	const hasFilters = filters.activeCount > 0;

	const sortState: SortState<SortKeyOf<S>> | null = search.sort ? { key: search.sort as SortKeyOf<S>, dir: search.dir ?? "asc" } : null;

	const pagination: PaginationProps = {
		page,
		pageSize,
		total: list.data?.total ?? 0,
		labels: {
			summary: ({ from, to, total }) => summary.range({ count: total, from: fmt(from), to: fmt(to), total: fmt(total) }),
			empty: summary.empty,
		},
		onPageChange: (p) => void navigate({ search: (prev) => ({ ...prev, page: p === 1 ? undefined : p }) }),
		onPageSizeChange: (n) => patch({ page_size: n === PAGE_SIZE_DEFAULT ? undefined : n } as Partial<S>),
	};

	return {
		list,
		/** Write URL params (resets to page 1). */
		patch,
		/** The FilterBar's draft values and their setter. */
		draft: filters.draft,
		setDraft: filters.set,
		hasFilters,
		clearFilters: filters.reset,
		/** Spread onto `<FilterBar>`. */
		filterBar: { onApply: filters.apply, onReset: filters.reset, activeCount: filters.activeCount, busy: list.isFetching },
		/** Spread onto `<DataTable>`. */
		table: {
			rows: list.data?.items ?? [],
			loading: list.isFetching,
			error: list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null,
			sort: {
				state: sortState,
				// Accepts any key: the table only emits its own columns' keys, and a child
				// route's `S` (merged with its parent's search) can be narrower than them.
				onChange: (s: SortState<string> | null) => patch({ sort: s?.key, dir: s?.dir } as Partial<S>),
			},
			pagination,
			columnVisibility,
		},
		/** The table's empty state: "no matches" + a clear-filters button while filtered, "none yet" otherwise. */
		emptyState: (copy: { filtered: EmptyCopy; none: EmptyCopy; clearLabel: ReactNode }): EmptyState =>
			hasFilters
				? {
						...copy.filtered,
						action: (
							<button type="button" className="btn btn-sm btn-outline" onClick={filters.reset}>
								{copy.clearLabel}
							</button>
						),
					}
				: copy.none,
	};
}

// ── Keyset mode ─────────────────────────────────────────────────────────────

/**
 * The URL params of a keyset list:
 *   cursor  the page being shown (absent = the first page) — the API's opaque
 *           `next_cursor`, sent back exactly as received
 *   prev    the cursors of the pages before it, oldest first, WITHOUT the
 *           first page (which has none). "Prev" pops it; "Next" pushes the
 *           current cursor. A JSON array in the address bar (TanStack's search
 *           serialiser) — reload / back / a shared link land on the same page.
 *   limit   rows per page (the wire's own name for it)
 * `keysetSearch()` (list-search.ts) builds the lenient schema.
 */
export interface KeysetSearchParams {
	cursor?: string;
	prev?: string[];
	limit?: number;
}

/** A keyset page as the API sends it. `total` comes with the first page (no cursor) only. */
export interface KeysetPage<Row> {
	items: Row[];
	next_cursor: string | null;
	total: number | null;
}

/** The pager line in this list's words; the numbers arrive formatted, `count` (raw total) picks the plural. */
export interface KeysetSummary {
	showing?: (values: SummaryValues) => string;
	showingNoTotal?: (values: { from: string; to: string }) => string;
	empty?: string;
}

/** Deep-linked pages beyond this many keep only the most recent cursors; "Prev" past them goes to the first page. */
export const KEYSET_PREV_MAX = 25;

export interface UseKeysetListPageOptions<S extends KeysetSearchParams, Row, Key extends QueryKey> {
	paging: "keyset";
	/** `Route.useSearch()`. */
	search: S;
	navigate: SearchNavigate<NoInfer<S>>;
	/** The page's `queryOptions` factory; spread `q` (cursor + limit) and add the page's own filters. */
	listOptions: (q: { cursor?: string; limit: number }) => UseQueryOptions<KeysetPage<Row>, Error, KeysetPage<Row>, Key>;
	/** localStorage key for the Columns picker — never rename one, or saved choices are lost. */
	columnsKey: string;
	pageSizes: readonly number[];
	defaultPageSize: number;
	/**
	 * The total when the first page was never seen in this tab (a reload on
	 * page 3) — e.g. the same filters' summary count. Optional.
	 */
	totalFallback?: number | null;
	summary?: KeysetSummary;
}

export interface KeysetListPage<Row> {
	list: ReturnType<typeof useQuery<KeysetPage<Row>, Error, KeysetPage<Row>>>;
	rows: Row[];
	/** The total for the current filters, kept from the first page while paging; null if unknown. */
	total: number | null;
	/** Write URL params; back to the first page. */
	patch: (next: Record<string, unknown>) => void;
	/** Spread onto `<KeysetPager>` (add `position` / `start`). */
	pager: KeysetPagerProps;
	/** Spread onto `<DataTable>` (with `pager={(p) => <KeysetPager {...page.pager} position={p} />}`). */
	table: {
		rows: Row[];
		loading: boolean;
		error: { message: string; onRetry: () => void } | null;
		columnVisibility: ReturnType<typeof useColumnVisibility>;
	};
}

function useKeysetListPage<S extends KeysetSearchParams, Row, Key extends QueryKey>({
	search,
	navigate,
	listOptions,
	columnsKey,
	pageSizes,
	defaultPageSize,
	totalFallback,
	summary,
}: UseKeysetListPageOptions<S, Row, Key>): KeysetListPage<Row> {
	const limit = search.limit && pageSizes.includes(search.limit) ? search.limit : defaultPageSize;
	const cursor = search.cursor;
	const prev = cursor ? (search.prev ?? []) : [];
	const list = useQuery(listOptions({ cursor, limit }));
	const columnVisibility = useColumnVisibility(columnsKey);
	const { formatNumber: fmt } = useUiStrings();

	// The total arrives with the first page only. Keep it per result set (every
	// param but the cursor) so it survives Next / Prev, and a filter change
	// never shows the previous filter's total.
	const { cursor: _c, prev: _p, ...rest } = search;
	const setKey = JSON.stringify({ ...rest, limit: undefined });
	const totals = useRef(new Map<string, number>());
	if (!cursor && list.data && list.data.total !== null && !list.isPlaceholderData) totals.current.set(setKey, list.data.total);
	const total = totals.current.get(setKey) ?? totalFallback ?? null;

	const patch = useCallback(
		(next: Record<string, unknown>) =>
			void navigate({ search: (p) => ({ ...p, cursor: undefined, prev: undefined, ...next }) as S, replace: true }),
		[navigate],
	);

	const rows = list.data?.items ?? [];
	// Page index from the URL: the first page has no cursor; page n carries n-1 earlier cursors.
	const pageIndex = cursor ? prev.length + 1 : 0;
	const nextCursor = list.data?.next_cursor ?? null;

	const pager: KeysetPagerProps = {
		from: pageIndex * limit + 1,
		count: rows.length,
		total,
		hasPrevious: !!cursor,
		hasNext: nextCursor !== null,
		busy: list.isFetching,
		pageSize: limit,
		pageSizeOptions: pageSizes,
		onPageSizeChange: (n) => patch({ limit: n === defaultPageSize ? undefined : n }),
		// Paging pushes a history entry (Back = the previous page), like offset paging.
		onNext: () => {
			if (!nextCursor) return;
			const stack = cursor ? [...prev, cursor].slice(-KEYSET_PREV_MAX) : [];
			void navigate({ search: (p) => ({ ...p, cursor: nextCursor, prev: stack.length ? stack : undefined }) as S });
		},
		onPrevious: () => {
			if (!cursor) return;
			const back = prev.at(-1);
			const stack = prev.slice(0, -1);
			void navigate({ search: (p) => ({ ...p, cursor: back, prev: back && stack.length ? stack : undefined }) as S });
		},
		labels: {
			showing: summary?.showing ? ({ from, to, total: n }) => summary.showing!({ count: n, from: fmt(from), to: fmt(to), total: fmt(n) }) : undefined,
			showingNoTotal: summary?.showingNoTotal ? ({ from, to }) => summary.showingNoTotal!({ from: fmt(from), to: fmt(to) }) : undefined,
			empty: summary?.empty,
		},
	};

	return {
		list,
		rows,
		total,
		patch,
		pager,
		table: {
			rows,
			loading: list.isFetching,
			error: list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null,
			columnVisibility,
		},
	};
}
